import TextArea from 'antd/es/input/TextArea';
import { message } from 'antd';
import React, { MouseEventHandler, useCallback, useContext, useEffect, useState } from 'react';
import { Handle, NodeProps, NodeToolbar, Position, useReactFlow } from 'reactflow';
import HierarchyToolbarBottom from '../../components/hierarchy-toolbar/hierarchy-toolbar-bottom';
import HierarchyToolbarTop from '../../components/hierarchy-toolbar/hierarchy-toolbar-top';
import { InstanceMapContext } from '../../pages/canvas-view/canvas-flow';
import { Instance, InstanceMap, InstanceState } from '../../triggers/semantic-dive/semantic-dive';
import { uuid } from '../../utils';
import { duplicateNode } from '../node.helper';
import { createTopicNodeToReturn, getAllReferenceCopies } from '../topic-node/topic-node.helper';
import { TypeTopicNode } from '../topic-node/topic-node.model';
import { generateSubtopics } from '../../triggers/hierarchy-view/hierarchy-view.helper';
import { getStoredApiKey } from '../../components/ApiKeyModal';
import { createHierarchyNode, getChildHierarchyNodes } from './hierarchy-node.helper';
import { HierarchyNodeData, HierarchyNodeProps, hierarchyRootNodeId, TypeHierarchyNode } from './hierarchy-node.model';

import './hierarchy-node.scss'

export default function HierarchyNode({ data, id, xPos, yPos }: HierarchyNodeProps) {
  const reactFlowInstance = useReactFlow();

  const { instanceMap, setInstanceMap } = useContext(InstanceMapContext);

  const [onHover, setOnHover] = useState(false);
  const [topicName, setTopicName] = useState(data.topicNode.data.state.topic ?? `subtopic`);
  const [isEditing, setIsEditing] = useState(data.state.isEditing ?? false);
  const [isRecommended, setIsRecommended] = useState(data.state.isRecommended ?? false);

  // Start animating when recommended topic is clicked
  const [addingRecommendation, setAddingRecommendation] = useState(false);

  const [recommendedSubtopics, setRecommendedSubtopics] = useState<string[]>([]);
  const [generatingTopic, setGeneratingTopic] = useState(false);

  const handleChange = (event: any) => {
    setTopicName(event.target.value);
  }

  const generateNewSubtopics = useCallback(() => {
    if (recommendedSubtopics.length <= 0 && !generatingTopic) {
      setGeneratingTopic(true);
      const childTopicNames = getChildHierarchyNodes(data.topicNode.id, instanceMap)
        .map(instance => instance.topicNode.data.state.topic ?? '')
        .filter((topic): topic is string => !!topic);

      console.log('child', childTopicNames);
      return generateSubtopics(data.topicNode, childTopicNames).then(subtopics => {
        const firstTopic = subtopics.splice(0,1);
        setRecommendedSubtopics(subtopics);
        setGeneratingTopic(false);
        return firstTopic;
      });
    }
  }, [instanceMap, generatingTopic, recommendedSubtopics]);

  /**
   * Adds a new subtopic as a node, either recommended topic name or custom
   */
  const addSubtopic = useCallback(async (recommendTopics: boolean) => {
    let topicName = `subtopic of ${data.topicNode.data.state.topic}`;

    if (recommendTopics) {
      if (!getStoredApiKey()) {
        message.warning('API key is missing. You can set it in the Help menu.');
        return;
      }
      if (recommendedSubtopics.length > 0) {
        topicName = recommendedSubtopics.splice(0, 1)[0];
        setRecommendedSubtopics(recommendedSubtopics);
      } else {
        const response = (await generateNewSubtopics())?.splice(0, 1)[0];
        if (response) {
          topicName = response;
        }
      }
    }

    const newTopic: TypeTopicNode = createTopicNodeToReturn(
      topicName,
      data.topicNode.id,
      {
        x: window.innerWidth/2 + 700,
        y: window.innerHeight/2,
      },
      [],
      false
    );

    const hierarchyId = `hierarchy-${newTopic.id}`;

    reactFlowInstance.setNodes((nodes) =>
      nodes.concat([{
        id: hierarchyId,
        type: 'hierarchy',
        position: {
          x: xPos,
          y: yPos + 100
        },
        data: {
          topicNode: newTopic,
          state: {
            isEditing: !recommendTopics, // If recommended, not editing
          },
        } as HierarchyNodeData,
      }])
    );

    const newInstance: Instance = {
      parentId: data.topicNode.id,
      topicNode: newTopic,
      jsonObject: {
        nodes: [newTopic],
        edges: [],
        viewport: { x: 0, y: 0, zoom: 1 },
      },
      level: instanceMap[data.topicNode.data.parentId]?.level ?? 0,
      canvasImageURL: '',
      position: { x: 0, y: 0 },
    };
    instanceMap[newTopic.id] = newInstance;
    instanceMap[newTopic.data.parentId].jsonObject.nodes.push(duplicateNode(newTopic));
    setInstanceMap(instanceMap);

    // the edge between the clicked node and the child node is created
    reactFlowInstance.addEdges({ id: `hierarchy-edge-${newTopic.id}`, source: id, target: hierarchyId });

    // Focus in on textarea
    setTimeout(() => {
      const hierarchyNodeElement = document.querySelector(`[data-id="${hierarchyId}"]`) as HTMLElement
      (hierarchyNodeElement.getElementsByClassName('text-input')[0] as HTMLInputElement)?.focus();
    }, 100);
  },
  [instanceMap, recommendedSubtopics]);

  const addBroadTopic = useCallback(() => {
    // Create new instance
    const newInstance = createHierarchyNode(
      [instanceMap, setInstanceMap],
      reactFlowInstance,
    );

    // Add topic Node to child
    instanceMap[newInstance.topicNode.id] = newInstance;
    instanceMap[newInstance.topicNode.id].jsonObject.nodes.push(duplicateNode(data.topicNode));

    // Update parentId of child node
    data.topicNode.data.parentId = newInstance.topicNode.id;
    instanceMap[data.topicNode.id].parentId = newInstance.topicNode.id;
    setInstanceMap(instanceMap);

    // Add edge between new instance and this node
    reactFlowInstance.addEdges({
      id: `hierarchy-edge-${data.topicNode.id}`,
      source: `hierarchy-${newInstance.topicNode.id}`,
      target: `hierarchy-${data.topicNode.id}`,
    })

    // Find and remove edge between root and this node
    reactFlowInstance.setEdges(edges => edges.filter(edge =>
      !(edge.source === hierarchyRootNodeId && 
      edge.target === `hierarchy-${data.topicNode.id}`)
    ));

  },
  [instanceMap, recommendedSubtopics]);

  const beginEditing = useCallback(() => {
    setIsEditing(true);
    setTimeout(() => {
      document.getElementById('hierarchy-topic-textarea')?.focus();
    }, 1);
  }, [isEditing]);

  const endEditing = useCallback(() => {
    if (topicName.trim()) {
      document.getElementById('hierarchy-topic-textarea')?.blur();
      setIsEditing(false);
      updateTopicName();
    }
  }, [isEditing, topicName]);

  const updateTopicName = useCallback(() => {
    const references = getAllReferenceCopies(data.topicNode, instanceMap);
    // if it's a parent node of the child, 
    references.forEach(topicNode => {
      if (topicNode.id !== topicNode.data.originalId) {
        topicNode.data.instanceState = InstanceState.WAS;
      }
      topicNode.data.state.topic = topicName;
    });
    // console.log('word', data.topicNode.id, data.topicNode.data.originalId, references,instanceMap);
    setInstanceMap(instanceMap);
  }, [reactFlowInstance, instanceMap, topicName]);

  const handleEnterPress = (event: any) => {
    if(event.key === 'Enter'){
      endEditing();
    }
  }

  return (
    <div className={`hierarchy-node
      ${isRecommended ? 'recommended-topic': ''}
      ${addingRecommendation ? 'adding-recommendation' : ''}`}
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
      onClick={() => {
        if (isRecommended && !isEditing) {
          setIsRecommended(false);
          setAddingRecommendation(true);
        }
      }}
    >
      {
        isEditing ?
        <TextArea
        id='hierarchy-topic-textarea'
        className='text-input'
        value={topicName}
        onChange={handleChange}
        onBlur={endEditing}
        style={{
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
        }}
        autoSize
        onKeyDown={handleEnterPress}
        /> :
        <div className='topic-name'>{topicName}</div>
      }
      <NodeToolbar isVisible={!isRecommended && onHover} position={Position.Top}>
        <HierarchyToolbarTop
          hasParentTopic={!instanceMap[data.topicNode.id]?.parentId}
          addBroadTopic={addBroadTopic}
          isEditing={isEditing}
          toggleEditing={() => {!isEditing ? beginEditing() : endEditing()}}
        />
      </NodeToolbar>
      <NodeToolbar isVisible={!isRecommended && onHover} position={Position.Bottom}>
        <HierarchyToolbarBottom
          isEditing={isEditing}
          addCustomSubtopic={() => addSubtopic(false)}
          addGeneratedSubtopic={() => addSubtopic(true)}
        />
      </NodeToolbar>
      <Handle position={Position.Top} type="target" />
      <Handle position={Position.Bottom} type="source" />
    </div>
  );
}
